import { z } from "@brains/utils/zod";
import { isKeyOf } from "@brains/utils/object-keys";
import type {
  AtprotoLexicon,
  AtprotoLexiconObjectDef,
  AtprotoLexiconProperty,
  CanonicalAtprotoLexiconId,
} from "./lexicon";
import { canonicalAtprotoLexicons } from "./lexicon";
import { isRecord } from "@brains/utils/is-record";

interface AtprotoSchemaProperty extends AtprotoLexiconProperty {
  required?: string[] | undefined;
  properties?: Record<string, AtprotoSchemaProperty> | undefined;
  items?: AtprotoSchemaProperty | undefined;
  knownValues?: string[] | undefined;
  maxLength?: number | undefined;
  format?: string | undefined;
}

export type AtprotoRecordSchema = z.ZodType<Record<string, unknown>>;

// RFC 3339 date-time with required offset (Z or ±hh:mm), matching the AT
// Protocol `datetime` string format. Lenient Date.parse would accept "2026-01-01".
const RFC3339_DATETIME =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

function buildAtprotoStringSchema(
  property: AtprotoSchemaProperty,
): z.ZodType<string> {
  const baseSchema = z.string();
  let schema: z.ZodType<string> =
    property.maxLength !== undefined
      ? baseSchema.max(property.maxLength)
      : baseSchema;
  if (property.knownValues) {
    schema = schema.refine((value) => property.knownValues?.includes(value), {
      message: `expected one of ${property.knownValues.join(", ")}`,
    });
  }
  if (property.format === "datetime") {
    schema = schema.refine(
      (value) =>
        RFC3339_DATETIME.test(value) && !Number.isNaN(Date.parse(value)),
      { message: "expected datetime" },
    );
  }
  if (property.format === "uri") {
    schema = schema.refine(
      (value) => {
        try {
          new URL(value);
          return true;
        } catch {
          return false;
        }
      },
      { message: "expected uri" },
    );
  }
  return schema;
}

// Only local `#name` refs are supported; `main` is the record def, never a
// ref target. Anything else fails closed in the ref case below rather than
// falling through to an unvalidated field.
function resolveAtprotoLocalRef(
  ref: unknown,
  defs: AtprotoLexicon["defs"],
): AtprotoLexiconObjectDef | undefined {
  if (typeof ref !== "string" || !ref.startsWith("#")) return undefined;
  const def = defs[ref.slice(1)];
  return def?.type === "object" ? def : undefined;
}

function buildAtprotoFieldSchema(
  property: AtprotoSchemaProperty,
  defs: AtprotoLexicon["defs"],
): z.ZodType<unknown> {
  switch (property.type) {
    case "string":
      return buildAtprotoStringSchema(property);
    case "integer":
      return z.number().int();
    case "boolean":
      return z.boolean();
    case "array": {
      const itemSchema = property.items
        ? buildAtprotoFieldSchema(property.items, defs)
        : z.unknown();
      let schema = z.array(itemSchema);
      if (property.maxLength !== undefined) {
        schema = schema.max(property.maxLength);
      }
      return schema;
    }
    case "object":
      return buildAtprotoObjectSchema(property, defs);
    case "ref": {
      const target = resolveAtprotoLocalRef(property["ref"], defs);
      if (!target) {
        return z.custom<never>(() => false, {
          message: `unresolvable lexicon ref ${String(property["ref"])}`,
        });
      }
      return z.lazy(() => buildAtprotoObjectSchema(target, defs));
    }
    case "blob":
      return z.custom<Record<string, unknown>>(isRecord, {
        message: "expected blob",
      });
    default:
      return z.unknown();
  }
}

function buildAtprotoObjectShape(
  property: AtprotoSchemaProperty | AtprotoLexiconObjectDef,
  defs: AtprotoLexicon["defs"],
): Record<string, z.ZodType<unknown>> {
  const requiredFields = new Set(property.required ?? []);
  const shape: Record<string, z.ZodType<unknown>> = {};
  for (const [field, fieldProperty] of Object.entries(
    property.properties ?? {},
  )) {
    const fieldSchema = buildAtprotoFieldSchema(fieldProperty, defs);
    shape[field] = requiredFields.has(field)
      ? fieldSchema
      : fieldSchema.optional();
  }
  return shape;
}

// Records are a wire contract: a field the lexicon does not declare has no
// agreed meaning, so it is rejected rather than carried along. The allowed key
// set comes from the lexicon itself, which is why this holds for nested objects
// and for lexicons added later without anyone maintaining a list.
function buildAtprotoObjectSchema(
  property: AtprotoSchemaProperty | AtprotoLexiconObjectDef,
  defs: AtprotoLexicon["defs"],
): AtprotoRecordSchema {
  return z.strictObject(buildAtprotoObjectShape(property, defs));
}

export function buildAtprotoRecordSchema(
  lexicon: AtprotoLexicon,
): AtprotoRecordSchema {
  return z.strictObject({
    ...buildAtprotoObjectShape(lexicon.defs.main.record, lexicon.defs),
    $type: z.literal(lexicon.id).optional(),
  });
}

export type CanonicalAtprotoRecordSchemaId = CanonicalAtprotoLexiconId;

export const canonicalAtprotoRecordSchemas: Record<
  CanonicalAtprotoRecordSchemaId,
  AtprotoRecordSchema
> = {
  "ai.rizom.brain.card": buildAtprotoRecordSchema(
    canonicalAtprotoLexicons["ai.rizom.brain.card"],
  ),
  "ai.rizom.brain.deck": buildAtprotoRecordSchema(
    canonicalAtprotoLexicons["ai.rizom.brain.deck"],
  ),
  "ai.rizom.brain.link": buildAtprotoRecordSchema(
    canonicalAtprotoLexicons["ai.rizom.brain.link"],
  ),
  "ai.rizom.brain.note": buildAtprotoRecordSchema(
    canonicalAtprotoLexicons["ai.rizom.brain.note"],
  ),
  "ai.rizom.brain.post": buildAtprotoRecordSchema(
    canonicalAtprotoLexicons["ai.rizom.brain.post"],
  ),
  "ai.rizom.brain.project": buildAtprotoRecordSchema(
    canonicalAtprotoLexicons["ai.rizom.brain.project"],
  ),
  "ai.rizom.brain.series": buildAtprotoRecordSchema(
    canonicalAtprotoLexicons["ai.rizom.brain.series"],
  ),
  "ai.rizom.brain.socialPost": buildAtprotoRecordSchema(
    canonicalAtprotoLexicons["ai.rizom.brain.socialPost"],
  ),
  "ai.rizom.brain.topic": buildAtprotoRecordSchema(
    canonicalAtprotoLexicons["ai.rizom.brain.topic"],
  ),
};

export function listCanonicalAtprotoRecordSchemas(): AtprotoRecordSchema[] {
  return Object.values(canonicalAtprotoRecordSchemas);
}

export function getCanonicalAtprotoRecordSchema(
  id: string,
): AtprotoRecordSchema | undefined {
  if (!isKeyOf(canonicalAtprotoRecordSchemas, id)) return undefined;
  return canonicalAtprotoRecordSchemas[id];
}

function getRecordPathValue(
  record: Record<string, unknown>,
  path: readonly PropertyKey[],
): unknown {
  let value: unknown = record;
  for (const segment of path) {
    if (typeof segment === "symbol") return undefined;
    if (Array.isArray(value)) {
      value = value[Number(segment)];
      continue;
    }
    if (!isRecord(value)) return undefined;
    value = value[String(segment)];
  }
  return value;
}

function formatAtprotoSchemaIssue(
  lexicon: AtprotoLexicon,
  record: Record<string, unknown>,
  issue: z.ZodIssue,
): string {
  const path = issue.path.join(".");
  if (path === "$type") {
    return `AT Protocol record $type must match lexicon id: ${String(
      record["$type"],
    )} !== ${lexicon.id}`;
  }
  if (
    issue.code === "invalid_type" &&
    getRecordPathValue(record, issue.path) === undefined
  ) {
    return `Missing required AT Protocol record field: ${path}`;
  }
  if (issue.code === "invalid_type") {
    return `Invalid AT Protocol record field ${path}: expected ${issue.expected}`;
  }
  if (issue.code === "too_big") {
    return `Invalid AT Protocol record field ${path}: exceeds maxLength ${issue.maximum}`;
  }
  if (issue.code === "custom") {
    return `Invalid AT Protocol record field ${path}: ${issue.message}`;
  }
  if (issue.code === "unrecognized_keys") {
    return `Unrecognized AT Protocol record field(s): ${issue.keys.join(", ")}`;
  }
  return `Invalid AT Protocol record field ${path}: ${issue.message}`;
}

export function validateAtprotoRecord(
  lexicon: AtprotoLexicon,
  record: Record<string, unknown>,
): void {
  const result = buildAtprotoRecordSchema(lexicon).safeParse(record);
  if (result.success) return;
  const issue = result.error.issues[0];
  if (!issue) throw result.error;
  throw new Error(formatAtprotoSchemaIssue(lexicon, record, issue));
}
